import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useMatch } from 'react-router-dom';

import { useGetMatterQuery } from '@/services/api';

type DocumentTitleContextType = {
  /** Manually override the document title. */
  setTitle: (title: string) => void;
  /** Reset the title so that it once again follows the route. */
  resetTitle: () => void;
};

const noop = () => {};

const DocumentTitleContext = createContext<DocumentTitleContextType>({
  setTitle: noop,
  resetTitle: noop,
});

export const useDocumentTitle = (): DocumentTitleContextType =>
  useContext(DocumentTitleContext);

interface DocumentTitleProviderProps {
  children: React.ReactNode;
}

export const DocumentTitleProvider: React.FC<DocumentTitleProviderProps> = ({
  children,
}) => {
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  // Route matching using react-router's useMatch for clarity and accuracy
  const tasksRouteMatch = useMatch('/tasks/');

  const mattersRouteMatch = useMatch('/matters/');
  const matterRouteMatch = useMatch('/matters/:matterId/*');

  const rawMatterId = matterRouteMatch?.params?.matterId ?? null;

  // Only treat as a valid matter ID if it looks like a UUID (avoid matching reserved routes like /matters/create)
  const isValidUuid =
    rawMatterId &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      rawMatterId,
    );
  const matterId = isValidUuid ? rawMatterId : null;

  // Fetch matter details to display name in title
  const { data: matterData } = useGetMatterQuery(matterId ?? '', {
    skip: !matterId,
  });

  // Custom title override state
  const [customTitle, setCustomTitle] = useState<string | null>(null);

  const setTitle = useCallback((title: string) => {
    setCustomTitle(title);
  }, []);

  const resetTitle = useCallback(() => {
    setCustomTitle(null);
  }, []);

  // Compute and set title whenever relevant state changes
  useEffect(() => {
    let newTitle: string;

    if (matterData?.name && matterId === matterData?.id) {
      newTitle = `Finch • ${matterData.name}`;
    } else if (tasksRouteMatch) {
      newTitle = `Finch • Tasks`;
    } else if (mattersRouteMatch) {
      newTitle = `Finch • Matters`;
    } else {
      newTitle = 'Finch';
    }

    document.title = newTitle;
  }, [
    customTitle,
    matterId,
    matterData,
    searchParams,
    location.pathname,
    tasksRouteMatch,
    mattersRouteMatch,
  ]);

  const contextValue = useMemo(
    () => ({ setTitle, resetTitle }),
    [setTitle, resetTitle],
  );

  return (
    <DocumentTitleContext.Provider value={contextValue}>
      {children}
    </DocumentTitleContext.Provider>
  );
};
